import React, { useEffect, useState } from "react";
import {
  formatElapsedSince,
  formatStudioTime,
} from "../../utils/workforceTimeUtils";
import "./ShiftStatusBanner.css";

/**
 * ShiftStatusBanner — Region 1 of the three-region clock-screen body.
 *
 * Authoritative spec:
 *   - TIME-004 design §"Body (clocked in)" — "Clocked in since {HH:MM AM/PM MST}"
 *   - TIME-005 design §"Region 1 — Status line" — "On break since {HH:MM AM/PM MST}"
 *
 * The component is intentionally state-shape-agnostic: the parent passes in the
 * mode ("clocked-in" | "on-break") and the start instant. TIME-004a only ever
 * passes "clocked-in"; TIME-005 will start passing "on-break" once that story
 * lands. Component requires no change at that boundary — just the prop-value
 * flip from the parent (the swap rule TIME-005 §"Integration with TIME-004a"
 * defines).
 *
 * Live-region: aria-live="polite" wraps the two-line status block so SRs
 * announce mode transitions ("Clocked in since 09:14 AM MST" → "On break
 * since 11:42 AM MST"). Per the spec, the duration ticker updates only on
 * minute boundaries — we re-render every 30s but the formatted "HHh MMm"
 * string only changes when a new minute is crossed, which keeps SR spam at
 * approximately one announcement/minute.
 *
 * @param {Object} props
 * @param {"clocked-in"|"on-break"} [props.mode]  — defaults to "clocked-in"
 * @param {string|Date} props.startedAt  — ISO timestamp of shift start (clocked-in)
 *                                         OR break start (on-break)
 */
const ShiftStatusBanner = ({ mode = "clocked-in", startedAt }) => {
  const [, forceRerender] = useState(0);

  // Tick once a minute. We force a re-render on a 30s cadence (so the elapsed
  // string visibly catches up within at most 30s of a minute boundary) — the
  // formatted output only changes once per actual minute, so the DOM diff is
  // a no-op most of the time.
  useEffect(() => {
    const id = setInterval(() => {
      forceRerender((n) => n + 1);
    }, 30_000);
    return () => clearInterval(id);
  }, []);

  if (!startedAt) {
    return null;
  }

  const isOnBreak = mode === "on-break";
  const startedAtFormatted = formatStudioTime(startedAt);
  const elapsed = formatElapsedSince(startedAt);

  // Status copy. ";" is intentional — both lines are atomic per spec.
  const headlineText = isOnBreak
    ? `On break since ${startedAtFormatted}`
    : `Clocked in since ${startedAtFormatted}`;
  const elapsedText = isOnBreak
    ? `${elapsed} on break`
    : `${elapsed} elapsed`;

  return (
    <div
      className={`shift-status-banner shift-status-banner--${
        isOnBreak ? "on-break" : "clocked-in"
      }`}
      data-testid="shift-status-banner"
      role="status"
      aria-live="polite"
    >
      <div className="shift-status-banner__row">
        {isOnBreak ? (
          <span
            className="shift-status-banner__icon shift-status-banner__icon--pause"
            aria-hidden="true"
          >
            {/* Two vertical bars per TIME-005 §"Why pause icon, not amber dot" */}
            <span className="shift-status-banner__pause-bar" />
            <span className="shift-status-banner__pause-bar" />
          </span>
        ) : (
          <span
            className="shift-status-banner__icon shift-status-banner__icon--dot"
            aria-hidden="true"
          />
        )}
        <span className="shift-status-banner__headline">{headlineText}</span>
      </div>
      <div className="shift-status-banner__elapsed">{elapsedText}</div>
    </div>
  );
};

export default ShiftStatusBanner;
